import {
  ControlPanelPlacement,
  DeclarationHomeTab,
  DeclarationInterface,
  PanelPlacement,
  PreparationMode,
  Theme,
  type PatchUserUiPreferencesPayload,
  type UserUiPreferences,
} from '@/api'
import type { ThemePalettePreference } from '@/types/settings'
import type { InterfaceVariant } from '@/pages/documents/declaration/components/declarationTopBarTypes'
import type { DeclarationStepId } from '@/pages/documents/declaration/step/declarationSteps'

export const UI_PREFERENCES_CACHE_KEY = 'nabu.uiPreferences'
export const DECLARATION_VARIANT_STORAGE_KEY = 'nabu.declaration.variant'
export const APP_SETTINGS_STORAGE_KEY = 'app.settings'

export type OpenPlacement = 'floating' | 'left' | 'right'

export type NormalizedUiPreferences = {
  theme: ThemePalettePreference
  declarationInterface: 'classic' | 'beta'
  declarationBeta: {
    sourceDocument: PanelPlacement
    controlPanel: ControlPanelPlacement
    assistant: PanelPlacement
    homeTab: DeclarationStepId
  }
  /** Préparation présélectionnée (wizard, nouveau mail hook). `null` : préparation standard. */
  defaultPreparation: PreparationMode | null
  /** Annonces (bandeaux, nouveautés) fermées par l'utilisateur, sur tous ses postes. */
  dismissedAnnouncements: string[]
}

export const DEFAULT_UI_PREFERENCES: NormalizedUiPreferences = {
  theme: 'light',
  declarationInterface: 'classic',
  declarationBeta: {
    sourceDocument: PanelPlacement.Hidden,
    controlPanel: ControlPanelPlacement.Hidden,
    assistant: PanelPlacement.Hidden,
    homeTab: DeclarationHomeTab.Control,
  },
  defaultPreparation: null,
  dismissedAnnouncements: [],
}

const THEMES: ThemePalettePreference[] = ['light', 'dark', 'auto']
const INTERFACES = ['classic', 'beta'] as const
const PLACEMENTS = [
  PanelPlacement.Hidden,
  PanelPlacement.Floating,
  PanelPlacement.Left,
  PanelPlacement.Right,
] as const
// `tab` (panneau ancré dans l'onglet Contrôle) n'est plus proposé : une valeur `tab` déjà
// enregistrée retombe sur le défaut, fermé.
const CONTROL_PANEL_PLACEMENTS = Object.values(ControlPanelPlacement).filter(
  (placement) => placement !== ControlPanelPlacement.Tab
)
const PREPARATIONS: PreparationMode[] = ['classic', 'intelligent'] as PreparationMode[]
const HOME_TABS: DeclarationStepId[] = [
  'headers',
  'articles',
  'delivery',
  'regulation',
  'control',
]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && allowed.includes(value as T) ? (value as T) : fallback
}

export function normalizeUiPreferences(stored: unknown): NormalizedUiPreferences {
  const source = isRecord(stored) ? stored : {}
  const beta = isRecord(source.declarationBeta) ? source.declarationBeta : {}
  const defaults = DEFAULT_UI_PREFERENCES

  return {
    theme: oneOf(source.theme, THEMES, defaults.theme),
    declarationInterface: oneOf(source.declarationInterface, INTERFACES, defaults.declarationInterface),
    declarationBeta: {
      sourceDocument: oneOf(beta.sourceDocument, PLACEMENTS, defaults.declarationBeta.sourceDocument),
      controlPanel: oneOf(beta.controlPanel, CONTROL_PANEL_PLACEMENTS, defaults.declarationBeta.controlPanel),
      assistant: oneOf(beta.assistant, PLACEMENTS, defaults.declarationBeta.assistant),
      homeTab: oneOf(beta.homeTab, HOME_TABS, defaults.declarationBeta.homeTab),
    },
    defaultPreparation: PREPARATIONS.includes(source.defaultPreparation as PreparationMode)
      ? (source.defaultPreparation as PreparationMode)
      : defaults.defaultPreparation,
    dismissedAnnouncements: Array.isArray(source.dismissedAnnouncements)
      ? [...new Set(source.dismissedAnnouncements.filter((key): key is string => typeof key === 'string'))]
      : defaults.dismissedAnnouncements,
  }
}

export function mergeUiPreferences(
  current: NormalizedUiPreferences,
  patch: PatchUserUiPreferencesPayload
): NormalizedUiPreferences {
  return normalizeUiPreferences({
    theme: patch.theme ?? current.theme,
    declarationInterface: patch.declarationInterface ?? current.declarationInterface,
    declarationBeta: {
      ...current.declarationBeta,
      ...patch.declarationBeta,
    },
    // `null` efface le choix : on ne retombe pas sur la valeur courante.
    defaultPreparation:
      patch.defaultPreparation === undefined
        ? current.defaultPreparation
        : patch.defaultPreparation,
    // Comme la gateway : une fermeture s'ajoute aux autres, sans les remplacer.
    dismissedAnnouncements: patch.dismissAnnouncement
      ? [...current.dismissedAnnouncements, patch.dismissAnnouncement]
      : current.dismissedAnnouncements,
  })
}

export function toInterfaceVariant(value: NormalizedUiPreferences['declarationInterface']): InterfaceVariant {
  return value === 'beta' ? 'sections' : 'classic'
}

export function toDeclarationInterface(variant: InterfaceVariant): DeclarationInterface {
  return variant === 'sections' ? DeclarationInterface.Beta : DeclarationInterface.Classic
}

export function toTheme(value: ThemePalettePreference): Theme {
  if (value === 'dark') return Theme.Dark
  if (value === 'auto') return Theme.Auto
  return Theme.Light
}

export function readUiPreferencesCache(): NormalizedUiPreferences | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(UI_PREFERENCES_CACHE_KEY)
    if (!raw) return null
    return normalizeUiPreferences(JSON.parse(raw) as UserUiPreferences)
  } catch {
    return null
  }
}

export function writeUiPreferencesCache(preferences: NormalizedUiPreferences) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(UI_PREFERENCES_CACHE_KEY, JSON.stringify(preferences))
  } catch {
    // quota / private mode
  }
}

export function readStoredPaletteMode(): ThemePalettePreference | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(APP_SETTINGS_STORAGE_KEY)
    if (!raw) return null
    const mode = (JSON.parse(raw) as { paletteMode?: unknown }).paletteMode
    return oneOf(mode, THEMES, 'light') === mode ? (mode as ThemePalettePreference) : null
  } catch {
    return null
  }
}

export function readStoredDeclarationVariant(): InterfaceVariant {
  if (typeof window === 'undefined') return 'classic'
  return window.localStorage.getItem(DECLARATION_VARIANT_STORAGE_KEY) === 'sections'
    ? 'sections'
    : 'classic'
}

export function writeStoredDeclarationVariant(variant: InterfaceVariant) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DECLARATION_VARIANT_STORAGE_KEY, variant)
  } catch {
    // quota / private mode
  }
}

/**
 * Pousse le thème ou la vue déjà choisis dans le navigateur tant que le serveur est au défaut, une
 * seule fois : un navigateur qui a déjà lu les préférences du serveur (copie `nabu.uiPreferences`)
 * le laisse faire foi. Sinon, un retour en standard fait depuis la console (TEC-1702) serait annulé
 * au chargement suivant par l'ancienne valeur restée dans le navigateur.
 */
export function localOverridesWhenServerIsDefault(
  server: NormalizedUiPreferences
): PatchUserUiPreferencesPayload | null {
  if (readUiPreferencesCache() !== null) return null

  const patch: PatchUserUiPreferencesPayload = {}
  const localTheme = readStoredPaletteMode()
  const localVariant = readStoredDeclarationVariant()

  if (server.theme === 'light' && (localTheme === 'dark' || localTheme === 'auto')) {
    patch.theme = toTheme(localTheme)
  }
  if (server.declarationInterface === 'classic' && localVariant === 'sections') {
    patch.declarationInterface = DeclarationInterface.Beta
  }

  return patch.theme || patch.declarationInterface ? patch : null
}

export function placementToOpen(placement: PanelPlacement | ControlPanelPlacement): {
  open: boolean
  mode: 'floating' | 'docked'
  side: 'left' | 'right'
  reopen: OpenPlacement
} {
  const value: string = placement
  if (value === 'left' || value === 'right') {
    return { open: true, mode: 'docked', side: value, reopen: value }
  }
  if (value === 'floating') {
    return { open: true, mode: 'floating', side: 'right', reopen: 'floating' }
  }
  return { open: false, mode: 'floating', side: 'right', reopen: 'floating' }
}
